import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { Empty, Spin, Skeleton } from "antd";
import AdminHOC from "../../../components/shared/HOC/AdminHOC.jsx";
import CustomButton from '../../../components/global/Button/CustomButton';
import CommonTable from '../../../components/shared/table/Table.jsx';
import HistoryCard from './components/HistoryCard.jsx';
import totalEnrollment from "../../../assets/icons/dashboard/Total Enrollments.svg";
import totalCurriCulum from "../../../assets/icons/dashboard/TotalCurriculum.svg";
import totalGroup from "../../../assets/icons/dashboard/TotalGroups.svg";
import { userStats } from '../../../service/UserService.js';
import { showErrorMessage, showSuccessMessage } from '../../../components/shared/pagination/messages.js';
import CircularProgressChart from '../../../components/shared/charts/CircularProgressChart.jsx';
import { PIE_CHART_CONFIG } from '../../../utility/commonConstant.js';
import DeadlineTable from '../../../components/shared/table/DeadlinesTable.jsx';
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import { getUserReportFilterOptions } from '../../../service/AdminService/userService.js';
import CustomLabelDropdown from '../../../components/shared/new Form/Fields/CustomLabelDropdown.jsx';
import CustomInput from '../../../components/global/CustomInput/CustomInput.jsx';
import LottieIcon from '../../../components/shared/lottieIcon/LottieIcon.jsx';
import { getCourseColumns, getBundleColumns, contentColumns, getBundleCourseColumns } from '../../../assets/FormFeilds/UserHistoryCoulumns.js'
import { useUserHistory } from "../../../hooks/UserAdminHooks/UseUserHistory.js"
import usePagination from '../../../assets/hooks/PaginationHooks/usePagination.jsx';
import { downloadUserReport } from '../../../service/AdminService/userService.js';
import { STATUS_FILTER_OPTIONS, SUB_TYPE_OPTIONS, FILTER_TYPE_OPTIONS, DEFAULT_STATS } from '../../../assets/constants/admin/user.js';
import PageLoader from "../../../components/global/Loader/PageLoader.jsx"

const UserHistoryPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const location = useLocation();
    const isActive = location.state?.isActive ?? true;

    const defaultEnrollmentStatus = isActive ? "ACTIVE" : "INACTIVE";

    const getEnrollmentStatusOptions = (isActive) => [
        { value: isActive ? "ACTIVE" : "INACTIVE", label: isActive ? "Active" : "Inactive" },
        { value: "ALL", label: "All" },
        { value: isActive ? "INACTIVE" : "ACTIVE", label: isActive ? "Inactive" : "Active" },
    ];

    const [pageLoader, setPageLoader] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dashStatsData, setDashStatsData] = useState(DEFAULT_STATS);
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [isSearchHovered, setIsSearchHovered] = useState(false);
    const [expandedRowKey, setExpandedRowKey] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [userName, setUserName] = useState("N/A");

    const { data, fetchReport } = useUserHistory();

    const {
        currentPage, setCurrentPage, totalElements, setTotalElements,
        pageSize, setPageSize,
    } = usePagination();

    const [filters, setFilters] = useState({
        filterType: null,
        type: null,
        groupId: null,
        courseId: null,
        bundleId: null,
        status: null,
        enrollmentStatus: defaultEnrollmentStatus,
    });

    const [apiFilterOptions, setApiFilterOptions] = useState({
        groups: [],
        courses: [],
        bundles: [],
    });

    const toOptions = (arr = []) =>
        arr.map(({ id, name }) => ({ value: id, label: name }));

    const updateFilters = useCallback((updates) => {
        setFilters(prev => ({ ...prev, ...updates }));
        setCurrentPage(1);
    }, [setCurrentPage]);


    const fetchUserReport = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchReport(id, {
                page: currentPage - 1,
                size: pageSize,
                search: searchText.trim() || undefined,
                filterType: filters.filterType || undefined,
                type: filters.type || undefined,
                groupId: filters.groupId || undefined,
                courseId: filters.courseId || undefined,
                bundleId: filters.bundleId || undefined,
                status: filters.status || undefined,
                enrollmentStatus: filters.enrollmentStatus || undefined,
            });
            if (response.status === "SUCCESS") {
                setTotalElements(response.data.totalRecords || 0);
            }
        } catch (error) {
            showErrorMessage("Failed to Load Data");
        } finally {
            setLoading(false);
        }
    }, [id, currentPage, pageSize, searchText, filters, fetchReport, setTotalElements]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchUserReport();
        }, 500);
        return () => clearTimeout(handler);
    }, [fetchUserReport]);

    const loadInitialMetadata = useCallback(async () => {
        setFiltersLoading(true);
        try {
            const [stats, options] = await Promise.all([
                userStats(id, isActive),
                getUserReportFilterOptions(id)
            ]);

            setDashStatsData(stats.data.data);

            if (options.userDetails?.name) {
                setUserName(options.userDetails.name);
            }
            setApiFilterOptions({
                groups: options.groupNames || [],
                courses: options.courseNames || [],
                bundles: options.bundleNames || [],
            });
        } catch (err) {
            showErrorMessage("Failed to Load metadata");
        } finally {
            setFiltersLoading(false);
        }
    }, [id, isActive]);

    useEffect(() => {
        loadInitialMetadata();
    }, [loadInitialMetadata]);

    const handleDownload = async () => {
        setPageLoader(true);
        try {
            await downloadUserReport(id);
            showSuccessMessage("Report Downloaded Successfully!");
        } catch (err) {
            showErrorMessage("Failed to download report");
        } finally {
            setPageLoader(false);
        }
    };

    const handleTableChange = (pagination) => {
        if (!pagination) return;
        if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize);
            setCurrentPage(1);
        } else {
            setCurrentPage(pagination.current);
        }
        const scrollableNode = tableRef.current?.querySelector('.ant-table-body');
        if (scrollableNode) scrollableNode.scrollTo({ top: 0, behavior: 'auto' });
    };

    const piedata = PIE_CHART_CONFIG.map((item) => ({
        ...item,
        number: dashStatsData?.[item.key] || 0,
    }));

    const dashData = [
        { id: 1, header: "Total Enrollments", input: dashStatsData.enrollments, icon: totalEnrollment },
        { id: 2, header: "Total Groups", input: dashStatsData.groups, icon: totalGroup },
        { id: 3, header: "Total Curriculums", input: dashStatsData.bundles, icon: totalCurriCulum },
    ];

    const handleSearch = (query) => {
        setSearchText(query);
        setCurrentPage(1);
    };

    const enrollmentStatusOptions = getEnrollmentStatusOptions(isActive);

    return (
        <>
            <PageLoader visible={pageLoader} />
            <section className="w-[100%] max-w-full h-[130vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
                <header className="flex flex-col gap-6 mb-6">
                    <div className="flex justify-between items-center h-[50px]">
                        <h1 className="text-[#E74860] text-[28px] font-medium">
                            {!filtersLoading ? (
                                `${userName} Details`
                            ) : (
                                <Skeleton.Input active style={{ width: 220, height: 28, borderRadius: 6 }} />
                            )}
                        </h1>
                        <div className="flex justify-end items-center h-[46px] gap-3">
                            <CustomButton onClick={handleDownload}>Download</CustomButton>
                            <CustomButton onClick={() => navigate(`/quiz-report?userId=${id}&userName=${encodeURIComponent(userName)}`)}>Quiz Reports</CustomButton>
                        </div>
                    </div>
                </header>

                <div className="flex flex-row gap-4 bg-white">
                    <div className="flex gap-4">
                        {dashData.map((item) => (
                            <HistoryCard key={item.id} header={item.header} value={item.input} icon={item.icon} />
                        ))}
                    </div>
                    <div>
                        <CircularProgressChart data={piedata} />
                    </div>
                    <div className='w-full min-w-[280px] max-w-[400px] xl:max-w-[600px] 2xl:max-w-[1500px]'>
                        <DeadlineTable userId={id} />
                    </div>
                </div>

                <div className="w-full mt-6">
                    <div className="flex flex-wrap gap-4 justify-between mb-4">
                        <div onMouseEnter={() => setIsSearchHovered(true)} onMouseLeave={() => setIsSearchHovered(false)} className='items-start'>
                            <CustomInput
                                icon={isSearchHovered ? <LottieIcon name="searchIcon" size={24} shouldPlay={true} /> : <img src={searchIconsvg} alt="search" style={{ width: 24, height: 24 }} />}
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                placeholder="Search Courses or Curriculums"
                                allowClear
                                style={{ width: "20vw" }}
                            />
                        </div>

                        <div className="items-end flex flex-wrap gap-3">
                            <div style={{ width: "8vw" }}>
                                <CustomLabelDropdown
                                    value={filters.enrollmentStatus}
                                    onChange={(v) => updateFilters({ enrollmentStatus: v ?? null })}
                                    options={enrollmentStatusOptions}
                                    placeholder="All"

                                />
                            </div>

                            <div style={{ width: "10vw" }}>
                                <CustomLabelDropdown
                                    value={filters.filterType}
                                    onChange={(v) => updateFilters({ filterType: v ?? null, groupId: null })}
                                    options={FILTER_TYPE_OPTIONS}
                                    placeholder="All Enrollments"

                                />
                            </div>

                            {filters.filterType === "GROUP" && (
                                <div style={{ width: "12vw" }}>
                                    <CustomLabelDropdown
                                        value={filters.groupId}
                                        onChange={(v) => updateFilters({ groupId: v ?? null })}
                                        options={toOptions(apiFilterOptions.groups)}
                                        placeholder="Select Groups"
                                        loading={filtersLoading}
                                        allowClear
                                        showSearch
                                    />
                                </div>
                            )}

                            <div style={{ width: "10vw" }}>
                                <CustomLabelDropdown
                                    value={filters.type}
                                    onChange={(v) => updateFilters({ type: v ?? null, courseId: null, bundleId: null })}
                                    options={SUB_TYPE_OPTIONS}
                                    placeholder="All Types"
                                />
                            </div>

                            {filters.type === "COURSE" && (
                                <div style={{ width: "12vw" }}>
                                    <CustomLabelDropdown
                                        value={filters.courseId}
                                        onChange={(v) => updateFilters({ courseId: v ?? null })}
                                        options={toOptions(apiFilterOptions.courses)}
                                        placeholder="Select Courses"
                                        loading={filtersLoading}
                                        allowClear
                                        showSearch
                                    />
                                </div>
                            )}

                            {filters.type === "BUNDLE" && (
                                <div style={{ width: "12vw" }}>
                                    <CustomLabelDropdown
                                        value={filters.bundleId}
                                        onChange={(v) => updateFilters({ bundleId: v ?? null })}
                                        options={toOptions(apiFilterOptions.bundles)}
                                        placeholder="Select Curriculums"
                                        loading={filtersLoading}
                                        allowClear
                                        showSearch
                                    />
                                </div>
                            )}

                            <div style={{ width: "10vw" }}>
                                <CustomLabelDropdown
                                    value={filters.status}
                                    onChange={(v) => updateFilters({ status: v ?? null })}
                                    options={STATUS_FILTER_OPTIONS}
                                    placeholder="All Status"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div ref={tableRef}>
                    <CommonTable
                        data={data}
                        columns={getCourseColumns(expandedRowKey)}
                        showLoader={loading}
                        // rowKey="enrollmentId"
                        rowKey={(record) =>
                        record.type === "BUNDLE"
                        ? record.bundleId
                        : record.enrollmentId
                        }
                        serverSide={true}
                        className="hide-scrollbar"
                        scroll={{ y: "calc(100vh - 400px)" }}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalElements,
                        }}
                        onChange={handleTableChange}
                        onExpand={(expanded, record) => setExpandedRowKey(expanded ? (record.bundleId ?? record.enrollmentId) : null)}
                        expandedRowRender={(record) => {
                            if (record.type === "BUNDLE") {
                                return (
                                    <CommonTable
                                        data={record.courses}
                                        columns={getBundleCourseColumns(null)}
                                        // rowKey="enrollmentId"
                                        rowKey={(record) =>
                                        record.type === "BUNDLE"
                                        ? record.bundleId
                                        : record.enrollmentId
                                        }
                                        pagination={false}
                                        expandedRowRender={(courseRecord) => {
                                            return courseRecord.contentProgress?.length > 0 ? (
                                                <CommonTable
                                                    data={courseRecord.contentProgress}
                                                    columns={contentColumns}
                                                    rowKey="contentId"
                                                    pagination={false}
                                                />
                                            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                        }}
                                    />
                                );
                            }
                            return record.contentProgress?.length > 0 ? (
                                <CommonTable
                                    data={record.contentProgress}
                                    columns={contentColumns}
                                    rowKey="contentId"
                                    pagination={false}
                                />
                            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        }}
                    />
                </div>
            </section>
        </>
    );
};

export default AdminHOC(UserHistoryPage);